import {
    IconAlertCircle,
    IconCheck,
    IconChevronLeft,
    IconChevronRight,
    IconClipboard,
    IconRefresh
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { debug } from '../utils/debug';

/**
 * TimeMachine Component
 * Displays previous training examples in a carousel-like interface
 * Allows viewing and managing spam training history
 */
export default function QuickTrainTimeMachine({ locationId, onSelectExample = null }) {
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    spam: 0,
    legitimate: 0,
    spamRatio: 0,
  });

  useEffect(() => {
    if (!locationId) return;
    fetchTrainingExamples();
  }, [locationId]);

  const fetchTrainingExamples = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/spam-rules/${encodeURIComponent(locationId)}/training-examples`
      );
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);

      const data = await res.json();
      const examplesList = data.examples || data || [];

      setExamples(examplesList);
      setCurrentIndex(0);

      // Calculate stats
      const total = examplesList.length;
      const spam = examplesList.filter(e => e.is_spam).length;
      const legitimate = total - spam;
      const spamRatio = total > 0 ? ((spam / total) * 100).toFixed(1) : 0;

      setStats({ total, spam, legitimate, spamRatio });
    } catch (err) {
      debug.error('[TimeMachine] Failed to fetch examples:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentExample = examples[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? examples.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev === examples.length - 1 ? 0 : prev + 1));
  };

  const goToIndex = (idx) => {
    setCurrentIndex(Math.max(0, Math.min(idx, examples.length - 1)));
  };

  if (loading) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin">⏳</div>
          <span>Loading training examples...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 rounded-lg p-6">
        <h3 className="font-bold mb-2">⚠️ Error</h3>
        <p>{error}</p>
        <button
          onClick={fetchTrainingExamples}
          className="mt-4 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  if (examples.length === 0) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20 text-center">
        <div className="text-4xl mb-3">🔮</div>
        <h3 className="text-xl font-bold mb-2">No Training History</h3>
        <p className="opacity-80 mb-4">
          No training examples yet. Add examples using the Quick Train form to build your AI's knowledge.
        </p>
        <button
          onClick={fetchTrainingExamples}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold"
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs opacity-80">Total Examples</div>
        </div>
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.spam}</div>
          <div className="text-xs opacity-80">Spam</div>
        </div>
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.legitimate}</div>
          <div className="text-xs opacity-80">Legitimate</div>
        </div>
        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold">{stats.spamRatio}%</div>
          <div className="text-xs opacity-80">Spam Ratio</div>
        </div>
      </div>

      {/* Main Carousel with Time Machine Tunnel Effect */}
      <div className="relative overflow-hidden rounded-lg border border-white/20" style={{ perspective: '1000px' }}>
        {/* Background tunnel effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/30 via-purple-900/20 to-black pointer-events-none" />

        {/* 3D Tunnel - Previous examples receding */}
        <div className="relative h-96 flex items-center justify-center" style={{ transformStyle: 'preserve-3d' }}>
          {/* Receding examples (past) */}
          {examples.map((ex, idx) => {
            const relativeIdx = idx - currentIndex;
            const isPast = relativeIdx < 0;
            const isPresent = relativeIdx === 0;
            const isFuture = relativeIdx > 0;

            if (Math.abs(relativeIdx) > 3) return null; // Only show 3 items back/forward

            const depth = Math.abs(relativeIdx);
            const zDepth = isPast ? -depth * 150 : isFuture ? -depth * 150 : 0;
            const scale = Math.pow(0.75, depth);
            const opacity = 1 - depth * 0.25;

            return (
              <div
                key={idx}
                className={`absolute transition-all duration-300 cursor-pointer rounded-lg border ${
                  isPresent
                    ? 'border-blue-500 z-40'
                    : 'border-white/20'
                } ${isPast ? 'opacity-40' : ''}`}
                style={{
                  transform: `translateZ(${zDepth}px) scale(${scale})`,
                  opacity: isPresent ? 1 : opacity,
                  pointerEvents: isPresent ? 'auto' : 'none',
                  width: 'min(90%, 600px)',
                }}
                onClick={() => !isPresent && goToIndex(idx)}
              >
                <div className={`p-4 rounded-lg ${
                  isPresent ? 'bg-gray-800/70 backdrop-blur-sm' : 'bg-gray-900/50'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${
                      ex.is_spam
                        ? 'bg-red-500/30 text-red-200'
                        : 'bg-green-500/30 text-green-200'
                    }`}>
                      {ex.is_spam ? (
                        <><IconAlertCircle size={14} /> SPAM</>
                      ) : (
                        <IconCheck size={14} />
                      )}
                    </span>
                    {ex.confidence && (
                      <span className="text-xs opacity-70">{(ex.confidence * 100).toFixed(0)}%</span>
                    )}
                  </div>
                  {isPresent && (
                    <p className="text-xs opacity-80 truncate">{ex.subject || ex.sender_name}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation arrows */}
        <div className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none">
          <button
            onClick={goToPrevious}
            className="px-3 py-2 rounded-lg bg-gray-700/80 hover:bg-gray-600 text-white font-semibold pointer-events-auto transition-colors flex items-center gap-1"
          >
            <IconChevronLeft size={20} /> Prev
          </button>
          <button
            onClick={goToNext}
            className="px-3 py-2 rounded-lg bg-gray-700/80 hover:bg-gray-600 text-white font-semibold pointer-events-auto transition-colors flex items-center gap-1"
          >
            Next <IconChevronRight size={20} />
          </button>
        </div>

        {/* Progress indicator */}
        <div className="absolute bottom-2 left-4 right-4 text-center text-xs opacity-70">
          {currentIndex + 1} / {examples.length}
        </div>
      </div>

      {/* Current Example Detail Card */}
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
        {/* Example Card */}
        <div className="bg-gray-800/50 rounded-lg p-6 border border-white/10 mb-4">
          {/* Label */}
          <div className="flex items-center gap-2 mb-4">
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 ${
                currentExample.is_spam
                  ? 'bg-red-500/30 text-red-200 border border-red-500'
                  : 'bg-green-500/30 text-green-200 border border-green-500'
              }`}
            >
              {currentExample.is_spam ? (
                <><IconAlertCircle size={18} /> SPAM</>
              ) : (
                <><IconCheck size={18} /> LEGITIMATE</>
              )}
            </span>
            {currentExample.confidence && (
              <span className="text-sm opacity-70">
                Confidence: {(currentExample.confidence * 100).toFixed(0)}%
              </span>
            )}
            {currentExample.marked_at && (
              <span className="text-xs opacity-60 ml-auto">
                {new Date(currentExample.marked_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Example Content */}
          <div className="space-y-3">
            {currentExample.sender_name && (
              <div>
                <label className="text-xs font-semibold opacity-80 uppercase">From Name</label>
                <p className="text-white font-mono text-sm break-words">
                  {currentExample.sender_name}
                </p>
              </div>
            )}

            {currentExample.sender_email && (
              <div>
                <label className="text-xs font-semibold opacity-80 uppercase">From Email</label>
                <p className="text-white font-mono text-sm break-words">
                  {currentExample.sender_email}
                </p>
              </div>
            )}

            {currentExample.subject && (
              <div>
                <label className="text-xs font-semibold opacity-80 uppercase">Subject</label>
                <p className="text-white font-mono text-sm break-words">
                  {currentExample.subject}
                </p>
              </div>
            )}

            {currentExample.body_preview && (
              <div>
                <label className="text-xs font-semibold opacity-80 uppercase">Body</label>
                <p className="text-white font-mono text-sm break-words whitespace-pre-wrap">
                  {currentExample.body_preview}
                </p>
              </div>
            )}

            {!currentExample.sender_name &&
              !currentExample.sender_email &&
              !currentExample.subject &&
              !currentExample.body_preview && (
                <p className="text-gray-400 italic">No content provided for this example.</p>
              )}
          </div>
        </div>

        {/* Thumbnail Grid */}
        <div className="space-y-2">
          <label className="text-xs font-semibold opacity-80 uppercase">Quick Jump</label>
          <div className="grid grid-cols-10 gap-1 max-h-20 overflow-y-auto">
            {examples.map((example, idx) => (
              <button
                key={idx}
                onClick={() => goToIndex(idx)}
                className={`aspect-square rounded text-xs font-bold transition-all ${
                  idx === currentIndex
                    ? 'ring-2 ring-blue-400 bg-blue-500/40'
                    : example.is_spam
                    ? 'bg-red-500/30 hover:bg-red-500/50'
                    : 'bg-green-500/30 hover:bg-green-500/50'
                }`}
                title={`${example.is_spam ? 'Spam' : 'Legitimate'} - ${idx + 1}`}
              >
                {example.is_spam ? '🚨' : '✅'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={fetchTrainingExamples}
          className="flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center justify-center gap-2"
        >
          <IconRefresh size={20} />
          Refresh
        </button>

        {onSelectExample && currentExample && (
          <button
            onClick={() => onSelectExample(currentExample)}
            className="flex-1 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center justify-center gap-2"
          >
            <IconClipboard size={20} />
            Use This Example
          </button>
        )}
      </div>
    </div>
  );
}
